import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { Formik } from "formik";
import * as Yup from "yup";

const selectMenuProps = {
  PaperProps: {
    style: {
      marginTop: "1.5px",
      maxHeight: 150,
      width: "auto",
      maxWidth: "100%",
    },
  },
};

const FormDetailsCard = ({
  selectedFile,
  onSubmit,
  onCancel,
  uploading,
  jurisdictionOptions = [],
  initialValues = {},
  isAlreadyUploaded = false,
  finalizedEditMode = false,
}) => {
  const validationSchema = Yup.object({
    state: finalizedEditMode
      ? Yup.string().notRequired()
      : Yup.string().required("State is required"),
    isStatewideForm: finalizedEditMode
      ? Yup.boolean().notRequired()
      : Yup.boolean().required(),
    county: Yup.string().when("isStatewideForm", {
      is: false,
      then: (schema) =>
        finalizedEditMode
          ? schema.notRequired()
          : schema.required("County is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    jurisdictionLevel: Yup.string().when("isStatewideForm", {
      is: false,
      then: (schema) =>
        finalizedEditMode
          ? schema.notRequired()
          : schema
              .oneOf(["county", "municipality"])
              .required("Jurisdiction level is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    municipality: Yup.string().when(["isStatewideForm", "jurisdictionLevel"], {
      is: (isStatewideForm, jurisdictionLevel) =>
        !isStatewideForm && jurisdictionLevel === "municipality",
      then: (schema) =>
        finalizedEditMode
          ? schema.notRequired()
          : schema.required("Municipality is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    docSize: isAlreadyUploaded
      ? Yup.string().required("Document size is required")
      : Yup.string().notRequired(),
    documentLabel: finalizedEditMode
      ? Yup.string().trim().required("Document label is required")
      : Yup.string().notRequired(),
    needNoc: Yup.boolean().required(),
  });

  const formContent = (
    <Box
      sx={{
        border: isAlreadyUploaded ? "none" : "1px solid #d3d3d3",
        p: isAlreadyUploaded ? 0 : 2,
        mt: isAlreadyUploaded ? 0 : 2,
        borderRadius: "10px",
        boxShadow: isAlreadyUploaded
          ? "none"
          : "2px 3px 2px 1px rgba(0, 0, 0, 0.1)",
      }}
    >
      {!isAlreadyUploaded && (
        <Typography
          textAlign="center"
          variant="h6"
          color="primary"
          sx={{ mb: 2 }}
        >
          {selectedFile?.name || selectedFile?.fileName}
        </Typography>
      )}

      <Formik
        initialValues={{
          state: initialValues.state || "fl",
          isStatewideForm:
            typeof initialValues.isStatewideForm === "boolean"
              ? initialValues.isStatewideForm
              : typeof selectedFile?.guessedFormDetails?.isStatewideForm ===
                  "boolean"
                ? selectedFile.guessedFormDetails.isStatewideForm
                : true,
          county:
            initialValues.county ||
            selectedFile?.guessedFormDetails?.county ||
            "",
          jurisdictionLevel:
            initialValues.jurisdictionLevel ||
            selectedFile?.guessedFormDetails?.jurisdictionLevel ||
            "county",
          municipality:
            initialValues.municipality ||
            selectedFile?.guessedFormDetails?.municipality ||
            "",
          mun_type:
            initialValues.mun_type ||
            selectedFile?.guessedFormDetails?.mun_type ||
            "",
          docSize: initialValues.docSize || "",
          documentLabel: initialValues.documentLabel || "",
          isNoc:
            typeof initialValues.isNoc === "boolean"
              ? initialValues.isNoc
              : false,
          permitType: selectedFile?.selected_permit_type || "",
          needNoc:
            typeof initialValues.needNoc === "boolean"
              ? initialValues.needNoc
              : false,
        }}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={(values) => {
          const { permitType, ...restValues } = values;
          const currentFormId =
            selectedFile?.formId || selectedFile?.form?.form_id || "";

          let permitPayload = {};
          if (isAlreadyUploaded) {
            const isRemoving = !permitType;

            const isStatewideForm = values?.isStatewideForm;
            const isMunicipalityLevel =
              values?.jurisdictionLevel === "municipality";

            const existingJurisdictionId = selectedFile?.jurisdictions_id || "";

            const permitJurisdictionId = isStatewideForm
              ? selectedFile?.state
              : isMunicipalityLevel
                ? `${selectedFile?.state}_${values?.county}`
                : existingJurisdictionId ||
                  `${selectedFile?.state}_${values?.county}`;
            const permitMunicipalityId = isMunicipalityLevel
              ? `${selectedFile?.state}_${values?.county}_${values?.municipality}`
              : "";

            permitPayload = {
              permitType: isRemoving
                ? selectedFile?.selected_permit_type
                : permitType,
              permitFormId: isRemoving ? "" : currentFormId,
              jurisdictionId: permitJurisdictionId || "",
              municipalityId: permitMunicipalityId || "",
            };
          }

          onSubmit(
            finalizedEditMode
              ? {
                  documentLabel: values.documentLabel,
                  docSize: values.docSize,
                  ...permitPayload,
                }
              : { ...restValues, ...permitPayload },
          );
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
        }) => (
          <Box component="form" onSubmit={handleSubmit}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
                alignItems: "start",
              }}
            >
              {finalizedEditMode ? (
                <>
                  <TextField
                    fullWidth
                    variant="filled"
                    name="documentLabel"
                    label="Document Label"
                    value={values.documentLabel}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.documentLabel && Boolean(errors.documentLabel)
                    }
                    helperText={touched.documentLabel && errors.documentLabel}
                  />
                  <FormControl
                    fullWidth
                    variant="filled"
                    error={touched.docSize && Boolean(errors.docSize)}
                  >
                    <InputLabel>Doc Size</InputLabel>
                    <Select
                      name="docSize"
                      label="Doc Size"
                      value={values.docSize}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      MenuProps={selectMenuProps}
                    >
                      <MenuItem value="letter">Letter</MenuItem>
                      <MenuItem value="legal">Legal</MenuItem>
                      <MenuItem value="landscape_letter">
                        Landscape Letter
                      </MenuItem>
                      <MenuItem value="landscape_legal">
                        Landscape Legal
                      </MenuItem>
                    </Select>
                    {touched.docSize && errors.docSize && (
                      <FormHelperText>{errors.docSize}</FormHelperText>
                    )}
                  </FormControl>
                  <FormControl fullWidth variant="filled">
                    <InputLabel>Permit Type</InputLabel>
                    <Select
                      name="permitType"
                      label="Permit Type"
                      value={values.permitType}
                      onChange={handleChange}
                      MenuProps={selectMenuProps}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {(selectedFile?.permit_types || []).map((pt) => (
                        <MenuItem key={pt.name} value={pt.name}>
                          {pt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select "None" to remove this form's permit-type mapping.
                    </FormHelperText>
                  </FormControl>
                </>
              ) : (
                <>
                  <FormControl
                    fullWidth
                    error={touched.state && Boolean(errors.state)}
                    variant="filled"
                  >
                    <InputLabel>State</InputLabel>
                    <Select
                      name="state"
                      value={values.state}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="State"
                      MenuProps={selectMenuProps}
                    >
                      <MenuItem value="fl">Florida</MenuItem>
                    </Select>
                    <FormHelperText>
                      {touched.state && errors.state}
                    </FormHelperText>
                  </FormControl>
                  <FormControl fullWidth variant="filled">
                    <InputLabel>Applies Statewide?</InputLabel>
                    <Select
                      name="isStatewideForm"
                      label="Applies Statewide?"
                      value={values.isStatewideForm ? "yes" : "no"}
                      onChange={(e) => {
                        const isStatewideForm = e.target.value === "yes";
                        handleChange({
                          target: {
                            name: "isStatewideForm",
                            value: isStatewideForm,
                          },
                        });

                        if (isStatewideForm) {
                          setFieldValue("county", "");
                          setFieldValue("jurisdictionLevel", "county");
                          setFieldValue("municipality", "");
                          setFieldValue("mun_type", "");
                        }
                      }}
                      MenuProps={selectMenuProps}
                    >
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </Select>
                  </FormControl>

                  {!values.isStatewideForm && (
                    <Autocomplete
                      options={jurisdictionOptions}
                      value={
                        jurisdictionOptions.find(
                          (item) => item.county_slug === values.county,
                        ) || null
                      }
                      onChange={(_, newValue) => {
                        setFieldValue("county", newValue?.county_slug || "");
                        setFieldValue("municipality", "");
                        setFieldValue("mun_type", "");
                      }}
                      onBlur={() => setFieldTouched("county", true)}
                      getOptionLabel={(option) => option?.county_name || ""}
                      isOptionEqualToValue={(option, value) =>
                        option?.county_slug === value?.county_slug
                      }
                      ListboxProps={{
                        style: {
                          maxHeight: 150,
                          overflowY: "auto",
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="County"
                          variant="filled"
                          error={touched.county && Boolean(errors.county)}
                          helperText={touched.county && errors.county}
                          fullWidth
                        />
                      )}
                    />
                  )}

                  {!values.isStatewideForm && (
                    <FormControl fullWidth variant="filled">
                      <InputLabel>Jurisdiction level</InputLabel>
                      <Select
                        name="jurisdictionLevel"
                        label="Jurisdiction level"
                        value={values.jurisdictionLevel}
                        onChange={(e) => {
                          handleChange(e);
                          if (e.target.value !== "municipality") {
                            setFieldValue("municipality", "");
                            setFieldValue("mun_type", "");
                          }
                        }}
                        MenuProps={selectMenuProps}
                      >
                        <MenuItem value="county">County</MenuItem>
                        <MenuItem value="municipality">Municipality</MenuItem>
                      </Select>
                      <FormHelperText>
                        {touched.jurisdictionLevel && errors.jurisdictionLevel}
                      </FormHelperText>
                    </FormControl>
                  )}

                  {!values.isStatewideForm &&
                    values.jurisdictionLevel === "municipality" && (
                      <Autocomplete
                        options={
                          jurisdictionOptions.find(
                            (item) => item.county_slug === values.county,
                          )?.municipality || []
                        }
                        value={
                          (
                            jurisdictionOptions.find(
                              (item) => item.county_slug === values.county,
                            )?.municipality || []
                          ).find(
                            (item) => item.mun_slug === values.municipality,
                          ) || null
                        }
                        onChange={(_, newValue) => {
                          setFieldValue(
                            "municipality",
                            newValue?.mun_slug || "",
                          );
                          setFieldValue("mun_type", newValue?.mun_type || "");
                        }}
                        onBlur={() => setFieldTouched("municipality", true)}
                        getOptionLabel={(option) => option?.mun_name || ""}
                        isOptionEqualToValue={(option, value) =>
                          option?.mun_slug === value?.mun_slug
                        }
                        ListboxProps={{
                          style: {
                            maxHeight: 150,
                            overflowY: "auto",
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="municipality"
                            label="Municipality"
                            variant="filled"
                            error={
                              touched.municipality &&
                              Boolean(errors.municipality)
                            }
                            helperText={
                              touched.municipality && errors.municipality
                            }
                            fullWidth
                          />
                        )}
                      />
                    )}

                  {isAlreadyUploaded && (
                    <>
                      <FormControl
                        fullWidth
                        variant="filled"
                        error={touched.docSize && Boolean(errors.docSize)}
                      >
                        <InputLabel>Doc Size</InputLabel>
                        <Select
                          name="docSize"
                          label="Doc Size"
                          value={values.docSize}
                          onChange={(e) => {
                            handleChange(e);
                          }}
                          MenuProps={selectMenuProps}
                          onBlur={handleBlur}
                        >
                          <MenuItem value="letter">Letter</MenuItem>
                          <MenuItem value="legal">Legal</MenuItem>
                          <MenuItem value="landscape_letter">
                            Landscape Letter
                          </MenuItem>
                          <MenuItem value="landscape_legal">
                            Landscape Legal
                          </MenuItem>
                        </Select>
                        {touched.docSize && errors.docSize && (
                          <FormHelperText>{errors.docSize}</FormHelperText>
                        )}
                      </FormControl>

                      <FormControl fullWidth variant="filled">
                        <InputLabel>Is Noc</InputLabel>
                        <Select
                          name="isNoc"
                          label="Is Noc"
                          value={values.isNoc}
                          onChange={(e) => {
                            handleChange(e);
                            if (e.target.value === true)
                              setFieldValue("needNoc", false);
                          }}
                          MenuProps={selectMenuProps}
                        >
                          <MenuItem value={true}>Yes</MenuItem>
                          <MenuItem value={false}>No</MenuItem>
                        </Select>
                        <FormHelperText>
                          {touched.isNoc && errors.isNoc}
                        </FormHelperText>
                      </FormControl>
                    </>
                  )}
                  {isAlreadyUploaded && (
                    <FormControl fullWidth variant="filled">
                      <InputLabel>Permit Type</InputLabel>
                      <Select
                        name="permitType"
                        label="Permit Type"
                        value={values.permitType}
                        onChange={handleChange}
                        MenuProps={selectMenuProps}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {(selectedFile?.permit_types || []).map((pt) => (
                          <MenuItem key={pt.name} value={pt.name}>
                            {pt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        Select "None" to remove this form's permit-type mapping.
                      </FormHelperText>
                    </FormControl>
                  )}
                  {!values.isNoc && isAlreadyUploaded && (
                    <FormControl fullWidth variant="filled">
                      <InputLabel>Need Noc</InputLabel>
                      <Select
                        name="needNoc"
                        label="Need Noc"
                        value={values.needNoc ? "yes" : "no"}
                        onChange={(e) => {
                          const needNOC = e.target.value === "yes";
                          handleChange({
                            target: {
                              name: "needNoc",
                              value: needNOC,
                            },
                          });
                        }}
                        MenuProps={selectMenuProps}
                      >
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                      </Select>
                      <FormHelperText>
                        Select "Yes" if the document requires an NOC.
                      </FormHelperText>
                    </FormControl>
                  )}
                </>
              )}
            </Box>

            <Box
              sx={{ display: "flex", justifyContent: "center", mt: 3, gap: 1 }}
            >
              <Button
                type="button"
                color="error"
                disabled={uploading}
                variant="text"
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="success"
                disabled={uploading}
              >
                {uploading
                  ? "Saving..."
                  : isAlreadyUploaded
                    ? "Save Settings"
                    : "Upload"}
              </Button>
            </Box>
          </Box>
        )}
      </Formik>
    </Box>
  );

  // Wrap in Dialog for already uploaded forms (Document Settings modal)
  if (isAlreadyUploaded) {
    return (
      <Dialog
        open={true}
        onClose={onCancel}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "10px",
            boxShadow: "2px 3px 2px 1px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h6" color="primary">
            Document Settings
          </Typography>
          <IconButton onClick={onCancel} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{formContent}</DialogContent>
      </Dialog>
    );
  }

  // For new uploads, render inline card as before (no dialog wrapper)
  return formContent;
};

export default FormDetailsCard;
